import PropTypes from "prop-types";

const AnswerReviewCard = ({ questionNumber, answer, onShowExplanation }) => {
  const {
    question,
    questionSnapshot,
    selectedAnswer,
    correctAnswer,
    isCorrect,
    marksObtained,
  } = answer;

  const reviewQuestion = questionSnapshot || question;

  if (!reviewQuestion) {
    return null;
  }

  // Support both string and object answers
  const selectedKey =
    typeof selectedAnswer === "string" ? selectedAnswer : selectedAnswer?.key;

  const correctKey =
    typeof correctAnswer === "string" ? correctAnswer : correctAnswer?.key;

  const explanation =
    question?.explanation || questionSnapshot?.explanation || "";

  const getOptionStyle = (optionKey) => {
    if (optionKey === correctKey) {
      return "border-green-500 bg-green-50 text-green-700";
    }

    if (optionKey === selectedKey && !isCorrect) {
      return "border-red-500 bg-red-50 text-red-700";
    }

    return "border-gray-300 text-gray-700";
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-5">
      {/* Question Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800">
          Question {questionNumber}
        </h3>

        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            isCorrect
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {isCorrect ? "Correct" : "Wrong"}
        </span>
      </div>

      {/* Question */}
      <p className="text-gray-700 mb-4 leading-relaxed">
        {reviewQuestion.questionText}
      </p>

      {/* Options */}
      <div className="space-y-2">
        {reviewQuestion.options?.map((option, index) => {
          const optionKey =
            typeof option === "string"
              ? String.fromCharCode(65 + index)
              : option.key || String.fromCharCode(65 + index);

          const optionText = typeof option === "string" ? option : option.text;

          return (
            <div
              key={option._id || optionKey}
              className={`border rounded-lg p-3 flex items-center gap-3 ${getOptionStyle(
                optionKey,
              )}`}
            >
              <span className="flex items-center justify-center h-7 w-7 rounded-full bg-gray-100 text-sm font-semibold">
                {optionKey}
              </span>

              <span>{optionText}</span>

              {optionKey === selectedKey && (
                <span className="ml-auto text-xs font-medium">Your Answer</span>
              )}

              {optionKey === correctKey && (
                <span className="ml-auto text-xs font-medium">
                  Correct Answer
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Marks */}
      <div className="mt-4 flex justify-between text-sm">
        <span className="text-gray-500">Marks Obtained</span>

        <span
          className={`font-semibold ${
            marksObtained > 0 ? "text-green-600" : "text-red-600"
          }`}
        >
          {marksObtained}
        </span>
      </div>

      {/* Explanation Button */}
      {explanation && (
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={() => onShowExplanation(explanation)}
            className="
              rounded-lg
              border
              border-blue-600
              px-4
              py-2
              text-sm
              font-medium
              text-blue-600
              hover:bg-blue-50
              transition
            "
          >
            View Explanation
          </button>
        </div>
      )}
    </div>
  );
};

AnswerReviewCard.propTypes = {
  questionNumber: PropTypes.number.isRequired,

  onShowExplanation: PropTypes.func.isRequired,

  answer: PropTypes.shape({
    question: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        _id: PropTypes.string,
        questionText: PropTypes.string,
        explanation: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
              _id: PropTypes.string,
              key: PropTypes.string,
              text: PropTypes.string,
            }),
          ]),
        ),
      }),
    ]),

    questionSnapshot: PropTypes.shape({
      questionText: PropTypes.string,
      explanation: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            _id: PropTypes.string,
            key: PropTypes.string,
            text: PropTypes.string,
          }),
        ]),
      ),
    }),

    selectedAnswer: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        _id: PropTypes.string,
        key: PropTypes.string,
        text: PropTypes.string,
      }),
    ]),

    correctAnswer: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        _id: PropTypes.string,
        key: PropTypes.string,
        text: PropTypes.string,
      }),
    ]),

    isCorrect: PropTypes.bool,
    marksObtained: PropTypes.number,
  }).isRequired,
};

export default AnswerReviewCard;
